import { createContext } from "react";
import { Session } from "@supabase/supabase-js";
import { UserData } from "@/types/user-data";
import { ApplyPendingInviteResult } from "@/services/inviteAcceptanceService";
import type { OAuthProviderId } from "@/config/oauth.config";

export interface AuthContextType {
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  userData: UserData | null;
  /** AE-198: result of applying a pending invite for this session, once resolved. Null until checked or if none applied. */
  inviteApplication: ApplyPendingInviteResult | null;
  /** Dev only: re-read local persona override after changing it in `DevPersonaSwitcher`. */
  bumpDevPersonaPreview?: () => void;
  fillUserData: (profileData: Omit<UserData, "created_at">) => Promise<void>;
  updateUserSurvey: (surveyData: UserData["survey"]) => Promise<void>;
  /** AE-207: rename the signed-in user and refresh the cached `users` row. */
  updateUserName: (name: { firstName: string; lastName?: string | null }) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  verifyOtp: (email: string, token: string) => Promise<{
    error: Error | null;
    /** Authenticated session on success; AE-175 returns this so callers can persist acceptance. */
    data: Session | null;
  }>;
  /** AE-206: start the provider redirect. Resolves after the browser is handed off. */
  signInWithOAuth: (provider: OAuthProviderId) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  /** AE-206: true once this browser session accepted the current policies through the OAuth gate. */
  policyAcceptedThisSession: boolean;
  markPolicyAcceptedThisSession: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  session: null,
  loading: true,
  isAuthenticated: false,
  userData: null,
  inviteApplication: null,
  bumpDevPersonaPreview: undefined,
  updateUserSurvey: async () => {},
  updateUserName: async () => {},
  signInWithMagicLink: async () => ({ error: null }),
  verifyOtp: async () => ({ error: null, data: null }),
  signInWithOAuth: async () => ({ error: null }),
  signOut: async () => {},
  fillUserData: async () => {},
  policyAcceptedThisSession: false,
  markPolicyAcceptedThisSession: () => {},
});
